// App.js — Reddit Lead‑Gen/Intent Signals (single‑file)
// Drop into CRA / Vite / CodeSandbox. Upload a CSV exported by your Colab CSE harvester.
// Supports (from your Colab Steps 2–4): title, snippet, link, displayLink, published_iso,
// platform, has_hashtag, intent_score, AccountName, CleanDomain. Also accepts: Query, DiscoveredAt.
// Adds: Provider inference (fallback from Title/Query), Subreddit extraction, Token mining,
// leaderboards, Filters, Export, and a Playbook tab.

import React, { useEffect, useMemo, useState } from "react";

/* ============================= LocalStorage keys ============================= */
const LS_ROWS = "REDDIT_INTENT_ROWS_V2";
const LS_FILTERS = "REDDIT_INTENT_FILTERS_V2";
const LS_PLAYBOOK = "REDDIT_INTENT_PLAYBOOK_V2";

/* ============================= Domain Knowledge ============================= */
// Extend as needed — matches your Colab dictionaries
const PROVIDER_SYNONYMS = [
  "TechTarget",
  "Intentsify",
  "Anteriad",
  "5x5data",
  "Foundry",
  "Madison Logic",
  "Integrate",
  "NetLine",
  "INFUSEmedia",
  "DemandScience",
  "Inbox Insight",
  "PureB2B",
  "BNZSA",
  "Activate",
  "BrightTALK",
  "HG Insights",
  "Bombora",
  "G2",
  "6sense",
  "Demandbase",
  "RollWorks",
  "ZoomInfo",
  "Cognism",
  "SalesIntel",
  "Apollo",
  "UserGems",
  "Clearbit",
  "Leadfeeder",
  "Dealfront",
];

const TOPIC_TOKENS = [
  // Program types & outcomes
  "content syndication",
  "lead generation",
  "pipeline generation",
  "MQL",
  "BANT",
  "SQL",
  "SAL",
  "MQM",
  "meetings set",
  // Pricing & commercials
  "CPL",
  "cost per lead",
  "fixed CPL",
  "pilot program",
  "insertion order",
  "IO",
  // Data quality & verification
  "lead validation",
  "email deliverability",
  "phone verification",
  "data enrichment",
  "firmographics",
  "technographics",
  "intent topics",
  "deduplication",
  // Compliance
  "GDPR",
  "CCPA",
  "SOC 2",
  "opt-in",
  "double opt-in",
  // Delivery & integrations
  "Convertr",
  "server-to-server",
  "Salesforce",
  "HubSpot",
  "Marketo",
  "Eloqua",
  // Strategy & targeting
  "ABM",
  "buyer intent",
  "named accounts",
  "orchestration",
  // Measurement
  "attribution",
  "incrementality",
  "pipeline influence",
  "ROI",
];

/* =============================== Root Component ============================= */
export default function App() {
  const [tab, setTab] = useState("insights"); // "insights" | "playbook"
  return (
    <div className="min-h-screen bg-bg text-ink">
      <Styles />
      <div className="tabbar">
        <div className="brand">Reddit · Lead‑Gen & Intent Signals</div>
        <button
          className={`tab ${tab === "insights" ? "active" : ""}`}
          onClick={() => setTab("insights")}
        >
          Insights
        </button>
        <button
          className={`tab ${tab === "playbook" ? "active" : ""}`}
          onClick={() => setTab("playbook")}
        >
          Playbook
        </button>
        <div className="ml-auto" />
      </div>
      {tab === "insights" ? <InsightsPage /> : <PlaybookPage />}
    </div>
  );
}

/* =============================== Insights Page ============================== */
function InsightsPage() {
  const [rowsRaw, setRowsRaw] = useState(() => {
    try {
      const cached = localStorage.getItem(LS_ROWS);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [filters, setFilters] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_FILTERS) || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_ROWS, JSON.stringify(rowsRaw || []));
    } catch {}
  }, [rowsRaw]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_FILTERS, JSON.stringify(filters || {}));
    } catch {}
  }, [filters]);

  const rows = useMemo(() => normalizeRows(rowsRaw), [rowsRaw]);

  // universes
  const providers = useMemo(
    () => uniqNonEmpty(rows.flatMap((r) => r._providers)).sort(),
    [rows]
  );
  const subs = useMemo(
    () => uniqNonEmpty(rows.map((r) => r._subreddit)).sort(),
    [rows]
  );
  const domains = useMemo(
    () => uniqNonEmpty(rows.map((r) => r.DisplayLink)).sort(),
    [rows]
  );
  const tokens = useMemo(
    () => uniqNonEmpty(rows.flatMap((r) => r._tokens)).sort(),
    [rows]
  );
  const platforms = useMemo(
    () => uniqNonEmpty(rows.map((r) => r.Platform)).sort(),
    [rows]
  );

  const filtered = useMemo(() => applyFilters(rows, filters), [rows, filters]);

  const stats = useMemo(() => {
    const count = filtered.length;
    const medianScore = median(filtered.map((r) => r.OnTargetScore || 0));
    const span = dateSpan(filtered.map((r) => r._dateObj).filter(Boolean));
    const byProvider = rank(countBy(filtered.flatMap((r) => r._providers)));
    const bySub = rank(countBy(filtered.map((r) => r._subreddit || "—")));
    return { count, medianScore, span, byProvider, bySub };
  }, [filtered]);

  const downloadView = () => {
    const header = [
      "OnTargetScore",
      "Title",
      "Snippet",
      "PostURL",
      "DisplayLink",
      "Query",
      "DiscoveredAt",
      "Providers",
      "Subreddit",
      "Tokens",
      "Platform",
    ];
    const matrix = filtered.map((r) => [
      r.OnTargetScore,
      r.Title,
      r.Snippet,
      r.PostURL,
      r.DisplayLink,
      r.Query,
      r.DiscoveredAt,
      (r._providers || []).join("|"),
      r._subreddit || "",
      (r._tokens || []).join("|"),
      r.Platform || "",
    ]);
    const csv = arrayToCsv([header, ...matrix]);
    downloadText(
      csv,
      `Reddit_Intent_${dateStamp()}.csv`,
      "text/csv;charset=utf-8"
    );
  };

  const addToPlaybook = (row) => {
    try {
      const bag = JSON.parse(localStorage.getItem(LS_PLAYBOOK) || "[]");
      const item = {
        id: `${row.PostURL}|${row.Title}`.slice(0, 180),
        title: row.Title,
        snippet: row.Snippet,
        url: row.PostURL,
        provider: (row._providers || [])[0] || "",
        subreddit: row._subreddit || "",
        tokens: row._tokens || [],
        date: row.DiscoveredAt || "",
        score: row.OnTargetScore || 0,
        platform: row.Platform || "",
      };
      if (!bag.find((b) => b.id === item.id)) {
        bag.push(item);
        localStorage.setItem(LS_PLAYBOOK, JSON.stringify(bag));
        alert("Added to Playbook!");
      } else {
        alert("Already added.");
      }
    } catch (e) {
      alert("Could not add to Playbook.");
    }
  };

  return (
    <>
      <header className="px-6 py-4">
        <h1 className="text-2xl font-bold">
          Reddit Signals — Lead‑Gen / Intent Providers
        </h1>
        <div className="text-muted text-sm">
          Upload your Reddit‑scoped CSE CSV. Supported columns:{" "}
          <code>
            title, snippet, link, displayLink, published_iso, platform,
            has_hashtag, intent_score, AccountName, CleanDomain
          </code>
          . Also accepted: <code>Query, DiscoveredAt</code>. The app
          auto‑extracts Provider(s), Subreddit, Platform, and Topic Tokens.
        </div>
      </header>

      {/* Upload */}
      <section className="card">
        <h3 className="mb-2">Upload Export</h3>
        <label className="uploader">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) =>
              readFile(e, (t) => {
                const { data } = parseCSV(t);
                setRowsRaw(data);
              })
            }
          />
          <div className="hint">CSV only. Auto‑saves locally (no server).</div>
        </label>
        <div className="flex gap-2 mt-2">
          {rows.length ? (
            <div className="ok">Loaded {rows.length} rows.</div>
          ) : null}
          {rows.length ? (
            <button
              className="btn secondary"
              onClick={() => {
                if (!window.confirm("Clear saved dataset?")) return;
                setRowsRaw([]);
                localStorage.removeItem(LS_ROWS);
              }}
            >
              Clear dataset
            </button>
          ) : null}
        </div>
      </section>

      {/* Facets */}
      <section className="card">
        <div className="filters flex flex-wrap items-center gap-3">
          <MultiSel
            label="Provider"
            options={providers}
            values={filters.providers || []}
            onChange={(vals) => setFilters({ ...filters, providers: vals })}
          />
          <Sel
            label="Subreddit"
            value={filters.sub || ""}
            onChange={(v) => setFilters({ ...filters, sub: v })}
            options={["", ...subs]}
          />
          <Sel
            label="Source Domain"
            value={filters.domain || ""}
            onChange={(v) => setFilters({ ...filters, domain: v })}
            options={["", ...domains]}
          />
          <Sel
            label="Platform"
            value={filters.platform || ""}
            onChange={(v) => setFilters({ ...filters, platform: v })}
            options={["", ...platforms]}
          />
          <Sel
            label="Recency"
            value={filters.recency || ""}
            onChange={(v) => setFilters({ ...filters, recency: v })}
            options={["", "7d", "30d", "90d"]}
          />
          <Sel
            label="Min Score"
            value={String(filters.minScore ?? 0)}
            onChange={(v) => setFilters({ ...filters, minScore: Number(v) })}
            options={["0", "1", "2", "3", "4", "5"]}
          />
        </div>
        <div
          className="filters flex flex-wrap items-center gap-3"
          style={{ marginTop: 8 }}
        >
          <input
            className="input"
            placeholder="Search title/snippet…"
            value={filters.q || ""}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            style={{ minWidth: 240 }}
          />
          <MultiSel
            label="Tokens"
            options={tokens}
            values={filters.tokens || []}
            onChange={(vals) => setFilters({ ...filters, tokens: vals })}
          />
          <button className="btn" onClick={downloadView}>
            Export current view
          </button>
        </div>
      </section>

      {/* KPIs */}
      <section className="grid md:grid-cols-4 gap-3 px-1">
        <KPI label="Rows (filtered)" value={stats.count} />
        <KPI label="Median Score" value={stats.medianScore} />
        <KPI label="Date span" value={stats.span || "—"} />
        <KPI label="Unique Providers" value={providers.length} />
      </section>

      {/* Leaderboards */}
      <section className="grid md:grid-cols-2 gap-3 px-1">
        <Card title="Top Providers (by posts)">
          <TinyList items={stats.byProvider.slice(0, 10)} />
        </Card>
        <Card title="Top Subreddits (by posts)">
          <TinyList items={stats.bySub.slice(0, 10)} />
        </Card>
      </section>

      {/* Table */}
      <section className="card">
        <div className="section-head">
          <h3>Results (ranked)</h3>
          <span className="text-muted text-xs">
            Rank: Score ↓, then recency ↓
          </span>
        </div>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th>Score</th>
                <th>Provider / Subreddit</th>
                <th>Title & Snippet</th>
                <th>Meta</th>
                <th style={{ width: 180 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 400).map((r, i) => (
                <tr key={r._id}>
                  <td className="text-muted">{i + 1}</td>
                  <td>
                    <strong>{r.OnTargetScore ?? 0}</strong>
                  </td>
                  <td>
                    <div className="title">
                      {(r._providers || []).join(" · ") || "—"}
                    </div>
                    <div className="text-xs text-muted">
                      {r._subreddit || "—"}
                    </div>
                  </td>
                  <td>
                    <div className="title">{r.Title || "—"}</div>
                    <div className="snippet text-sm">{r.Snippet || "—"}</div>
                    {r._tokens?.length ? (
                      <div className="text-xs" style={{ marginTop: 6 }}>
                        {(r._tokens || []).slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="pill"
                            style={{ marginRight: 6 }}
                          >
                            {t}
                          </span>
                        ))}
                        {r._tokens.length > 6 ? (
                          <span className="text-muted text-xs">
                            +{r._tokens.length - 6}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td className="text-xs">
                    <div className="pill">{r.Platform || "Web"}</div>
                    <div className="text-muted" style={{ marginTop: 6 }}>
                      {r.DisplayLink || "—"}
                    </div>
                    <div className="text-muted" style={{ marginTop: 6 }}>
                      {r.DiscoveredAt || ""}
                    </div>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {r.PostURL ? (
                      <a
                        className="btn secondary link-btn"
                        href={r.PostURL}
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginRight: 8 }}
                      >
                        Open ↗
                      </a>
                    ) : null}
                    <button className="btn" onClick={() => addToPlaybook(r)}>
                      Add to Playbook
                    </button>
                    {r.Query ? (
                      <div
                        className="text-xs text-muted"
                        style={{ marginTop: 6 }}
                      >
                        <em>Query:</em> {r.Query}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-muted text-sm" style={{ marginTop: 8 }}>
          Showing {Math.min(filtered.length, 400)} of {filtered.length}.
        </div>
      </section>
    </>
  );
}

/* ================================ Playbook Tab ============================= */
function PlaybookPage() {
  const [clips, setClips] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_PLAYBOOK) || "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(LS_PLAYBOOK, JSON.stringify(clips));
    } catch {}
  }, [clips]);
  const [note, setNote] = useState("");

  const removeClip = (id) =>
    setClips((prev) => prev.filter((c) => c.id !== id));
  const clearAll = () => {
    if (!window.confirm("Clear all clipped items?")) return;
    setClips([]);
  };

  const exportPlaybook = () => {
    const header = [
      "Provider",
      "Subreddit",
      "Score",
      "Date",
      "Title",
      "Snippet",
      "URL",
      "Tokens",
    ];
    const rows = clips.map((c) => [
      c.provider,
      c.subreddit,
      c.score,
      c.date,
      c.title,
      c.snippet,
      c.url,
      (c.tokens || []).join("|"),
    ]);
    const csv = arrayToCsv([header, ...rows]);
    downloadText(
      csv,
      `Reddit_Playbook_${dateStamp()}.csv`,
      "text/csv;charset=utf-8"
    );
  };

  return (
    <>
      <header className="px-6 py-4">
        <h1 className="text-2xl font-bold">Playbook</h1>
        <div className="text-muted text-sm">
          Clips you added from Insights. Draft meeting notes or export.
        </div>
      </header>

      <section className="card">
        <div className="section-head">
          <h3>Notes (editable)</h3>
          <div className="chips">
            <button className="btn secondary" onClick={exportPlaybook}>
              Export CSV
            </button>
            <button className="btn secondary" onClick={clearAll}>
              Clear clips
            </button>
          </div>
        </div>
        <textarea
          className="input"
          rows={8}
          placeholder="Talking points, comparisons, pricing threads, success/failure stories…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </section>

      <section className="card">
        <div className="section-head">
          <h3>Clips ({clips.length})</h3>
        </div>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th>Provider / Subreddit</th>
                <th>Title & Snippet</th>
                <th>Meta</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {clips.map((c, i) => (
                <tr key={c.id}>
                  <td className="text-muted">{i + 1}</td>
                  <td>
                    <div className="title">{c.provider || "—"}</div>
                    <div className="text-xs text-muted">
                      {c.subreddit || "—"}
                    </div>
                  </td>
                  <td>
                    <div className="title">{c.title || "—"}</div>
                    <div className="snippet text-sm">{c.snippet || "—"}</div>
                  </td>
                  <td className="text-xs text-muted">
                    Score {c.score} · {c.date || "—"}
                    {(c.tokens || []).length ? (
                      <div style={{ marginTop: 6 }}>
                        {(c.tokens || []).slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="pill"
                            style={{ marginRight: 6 }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {c.url ? (
                      <a
                        className="btn secondary link-btn"
                        href={c.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open ↗
                      </a>
                    ) : null}
                    <button
                      className="btn"
                      style={{ marginLeft: 8 }}
                      onClick={() => removeClip(c.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/* ================================ Helpers ================================= */
function normalizeRows(rows = []) {
  let i = 1;
  return (rows || [])
    .map((r) => {
      // Accept Colab names or UI names
      const Title = (r.title || r.Title || "").trim();
      const Snippet = (r.snippet || r.Snippet || "").trim();
      const PostURL = (r.link || r.PostURL || r.url || "").trim();
      const DisplayLink = (r.displayLink || r.DisplayLink || "").trim();
      const Query = (r.query || r.Query || "").trim();
      const DiscoveredAt = (
        r.published_iso ||
        r.DiscoveredAt ||
        r.date ||
        ""
      ).trim();

      // Score & Platform
      const OnTargetScore =
        Number(r.intent_score ?? r.score ?? r.OnTargetScore ?? 0) || 0;
      let Platform = (r.platform || r.Platform || "").trim();
      if (!Platform) Platform = detectPlatformFromUrl(DisplayLink, PostURL);

      // Subreddit
      const _subreddit = extractSubreddit(PostURL);

      // Providers: prefer AccountName, else infer from Title/Query
      const inferred = providerDetect(`${Title} \n ${Query}`);
      let _providers = [];
      if (r.AccountName && String(r.AccountName).trim())
        _providers.push(String(r.AccountName).trim());
      _providers.push(...inferred);
      _providers = Array.from(new Set(_providers));

      // Token mining from title/snippet
      const _tokens = tokenDetect(`${Title} \n ${Snippet} \n ${Query}`);

      const _dateObj = parseDateGuess(DiscoveredAt);

      return {
        _id: `${i++}_${PostURL.slice(0, 24)}`,
        Title,
        Snippet,
        PostURL,
        DisplayLink,
        Query,
        DiscoveredAt: DiscoveredAt || (_dateObj ? toISO(_dateObj) : ""),
        OnTargetScore,
        Platform,
        _subreddit,
        _providers,
        _tokens,
        _dateObj,
      };
    })
    .filter((r) => r.Title || r.Snippet || r.PostURL);
}

function providerDetect(text = "") {
  const t = String(text || "").toLowerCase();
  const hits = new Set();
  for (const p of PROVIDER_SYNONYMS) {
    const k = String(p).toLowerCase();
    if (!k) continue;
    if (t.includes(k)) hits.add(p);
  }
  return Array.from(hits);
}

function tokenDetect(text = "") {
  const t = String(text || "").toLowerCase();
  const hits = new Set();
  for (const tok of TOPIC_TOKENS) {
    const k = String(tok).toLowerCase();
    if (!k) continue;
    if (t.includes(k)) hits.add(tok);
  }
  return Array.from(hits);
}

function extractSubreddit(url = "") {
  try {
    const u = new URL(url);
    const parts = (u.pathname || "").split("/").filter(Boolean);
    const idx = parts.findIndex((p) => p.toLowerCase() === "r");
    if (idx >= 0 && parts[idx + 1]) return `r/${parts[idx + 1]}`;
  } catch {}
  return "";
}

function detectPlatformFromUrl(displayLink = "", url = "") {
  const host = (displayLink || safeURL(url).host || "").toLowerCase();
  if (host.includes("reddit.com")) return "Reddit";
  if (host.includes("news.ycombinator.com")) return "Hacker News";
  if (host.includes("twitter.com") || host.includes("x.com")) return "Twitter";
  if (host.includes("linkedin.com")) return "LinkedIn";
  if (host.includes("youtube.com")) return "YouTube";
  return host ? "Web" : "Web";
}
function safeURL(u = "") {
  try {
    return new URL(u);
  } catch {
    return { host: "" };
  }
}

function applyFilters(rows, f = {}) {
  let list = rows.slice();

  // multi facets
  if (f.providers?.length)
    list = list.filter((r) =>
      (r._providers || []).some((p) => f.providers.includes(p))
    );
  if (f.tokens?.length)
    list = list.filter((r) =>
      (r._tokens || []).some((t) => f.tokens.includes(t))
    );

  // singles
  if (f.sub) list = list.filter((r) => r._subreddit === f.sub);
  if (f.domain) list = list.filter((r) => r.DisplayLink === f.domain);
  if (f.platform) list = list.filter((r) => r.Platform === f.platform);
  if (f.recency) list = list.filter((r) => inRecency(r._dateObj, f.recency));
  if (f.minScore != null)
    list = list.filter((r) => (r.OnTargetScore || 0) >= (f.minScore || 0));

  // search
  if ((f.q || "").trim()) {
    const qq = f.q.trim().toLowerCase();
    list = list.filter(
      (r) =>
        (r.Title || "").toLowerCase().includes(qq) ||
        (r.Snippet || "").toLowerCase().includes(qq)
    );
  }

  // sort: score desc, date desc
  list.sort((a, b) => {
    const s = (b.OnTargetScore || 0) - (a.OnTargetScore || 0);
    if (s) return s;
    const tb = b._dateObj ? b._dateObj.getTime() : 0;
    const ta = a._dateObj ? a._dateObj.getTime() : 0;
    return tb - ta;
  });

  return list;
}

/* ===== CSV parsing (robust) ===== */
function parseCSV(text = "") {
  const rows = [];
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/);
  if (!lines.length) return { data: [] };
  const headers = splitCsvLine(lines[0]);
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const vals = splitCsvLine(line);
    const obj = {};
    headers.forEach((h, idx) => (obj[h] = vals[idx] ?? ""));
    rows.push(obj);
  }
  return { data: rows };
}
function splitCsvLine(line) {
  const out = [];
  let cur = "",
    inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else inQ = !inQ;
    } else if (ch === "," && !inQ) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

/* ==================== utility fns ==================== */
function uniqNonEmpty(arr = []) {
  const s = new Set();
  (arr || []).forEach((x) => {
    const v = (x || "").trim();
    if (v) s.add(v);
  });
  return Array.from(s);
}
function countBy(arr = [], fn = (x) => x) {
  const m = {};
  (arr || []).forEach((x) => {
    const k = fn(x) || "—";
    m[k] = (m[k] || 0) + 1;
  });
  return m;
}
function rank(obj = {}) {
  return Object.entries(obj)
    .map(([k, v]) => ({ label: k, value: v }))
    .sort((a, b) => b.value - a.value);
}
function parseDateGuess(s = "") {
  if (!s) return null;
  const t = Date.parse(s);
  if (!Number.isNaN(t)) return new Date(t);
  const m = String(s).match(/\b\d{4}-\d{2}-\d{2}\b/);
  if (m) {
    const t2 = Date.parse(m[0]);
    if (!Number.isNaN(t2)) return new Date(t2);
  }
  return null;
}
function inRecency(d, bucket) {
  if (!bucket) return true;
  if (!d) return false;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (bucket === "7d") return days <= 7;
  if (bucket === "30d") return days <= 30;
  if (bucket === "90d") return days <= 90;
  return true;
}
function median(nums = []) {
  const arr = (nums || []).slice().sort((a, b) => a - b);
  if (!arr.length) return 0;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : Math.round((arr[mid - 1] + arr[mid]) / 2);
}
function dateSpan(ds = []) {
  if (!ds.length) return "";
  const min = new Date(Math.min(...ds.map((d) => d.getTime())));
  const max = new Date(Math.max(...ds.map((d) => d.getTime())));
  return `${toISO(min)} → ${toISO(max)}`;
}
function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}
function arrayToCsv(matrix) {
  const BOM = "\uFEFF";
  const lines = matrix.map((row) => row.map(csvEscape).join(","));
  return BOM + lines.join("\r\n");
}
function csvEscape(val) {
  const s = String(val ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function readFile(e, onText) {
  const f = e.target.files?.[0];
  if (!f) return;
  const r = new FileReader();
  r.onload = (ev) => onText(String(ev.target.result || ""));
  r.readAsText(f);
}
function downloadText(text, filename, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function dateStamp() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}_${String(d.getHours()).padStart(
    2,
    "0"
  )}${String(d.getMinutes()).padStart(2, "0")}`;
}

/* ================================ UI bits ================================= */
function Sel({ label, value, onChange, options }) {
  return (
    <div className="select-wrap">
      <span className="sel-label">{label}:</span>
      <select
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {(options || [""]).map((opt) => (
          <option key={opt || "(All)"} value={opt}>
            {opt || "(All)"}
          </option>
        ))}
      </select>
    </div>
  );
}

function MultiSel({ label, options = [], values = [], onChange }) {
  const toggle = (opt) => {
    const set = new Set(values);
    set.has(opt) ? set.delete(opt) : set.add(opt);
    onChange(Array.from(set));
  };
  return (
    <div className="multisel">
      <div className="sel-label">{label}:</div>
      <div className="chips">
        {options.length ? (
          options.map((opt) => (
            <Chip
              key={opt}
              label={opt}
              active={values.includes(opt)}
              onClick={() => toggle(opt)}
            />
          ))
        ) : (
          <span className="text-muted text-sm">—</span>
        )}
      </div>
    </div>
  );
}

function Chip({ label, active, onClick }) {
  return (
    <button
      className={`chip ${active ? "on" : ""}`}
      onClick={onClick}
      title={label}
    >
      {label}
    </button>
  );
}

function KPI({ label, value }) {
  return (
    <div className="kpi">
      <div className="kpi-value">{String(value ?? "—")}</div>
      <div className="kpi-label">{label}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="subcard">
      <div className="subcard-head">
        <h4>{title}</h4>
      </div>
      {children}
    </div>
  );
}

function TinyList({ items = [] }) {
  if (!items.length) return <div className="text-muted text-sm">—</div>;
  return (
    <ul className="tinylist">
      {items.map((x) => (
        <li key={x.label}>
          <span className="tinylabel">{x.label}</span>
          <span className="tinyval">{x.value}</span>
        </li>
      ))}
    </ul>
  );
}

/* ================================= Styles ================================= */
function Styles() {
  const css = `
:root{
  --bg:#ffffff; --panel:#ffffff; --ink:#0f172a; --muted:#6b7280; --line:#e5e7eb;
  --accent:#22c55e; --accent-2:#16a34a; --link:#1f8ef1; --chip:#f8fafb;
}
*{box-sizing:border-box}
html,body,#root{min-height:100%} body{margin:0;background:var(--bg);color:var(--ink);font:14px/1.45 Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto}

.px-6{padding-left:24px;padding-right:24px} .py-4{padding-top:16px;padding-bottom:16px}
.text-muted{color:var(--muted)} .text-sm{font-size:13px} .text-2xl{font-size:24px}.font-bold{font-weight:700}
.grid{display:grid}.gap-3{gap:12px}.md\\:grid-cols-4{grid-template-columns:1fr}
@media(min-width:900px){.md\\:grid-cols-4{grid-template-columns:repeat(4,1fr)} .md\\:grid-cols-2{grid-template-columns:repeat(2,1fr)}}
.px-1{padding-left:4px;padding-right:4px}
.flex{display:flex}.gap-2{gap:8px}.mt-2{margin-top:8px}.ml-auto{margin-left:auto}

.tabbar{display:flex;gap:8px;padding:10px 12px;border-bottom:1px solid var(--line);position:sticky;top:0;background:var(--bg);z-index:3}
.tab{border:1px solid var(--line);background:#fff;border-radius:999px;padding:6px 12px;cursor:pointer}
.tab.active{background:#eef6ff;border-color:#b3d4f3;color:#0a66c2}
.brand{font-weight:700;margin-right:8px;align-self:center}

.card{background:var(--panel);border:1px solid var(--line);border-radius:16px;padding:16px;margin:12px;box-shadow:0 1px 3px rgba(17,24,39,.06),0 1px 2px rgba(17,24,39,.04)}
.subcard{background:#f8fafb;border:1px solid var(--line);border-radius:12px;padding:12px}
.subcard-head h4{margin:0 0 8px 0}

.kpi{background:#f8fafb;border:1px solid var(--line);border-radius:12px;padding:12px;margin:12px}
.kpi-value{font-size:22px;font-weight:700} .kpi-label{color:var(--muted);font-size:12px}

.uploader input[type=file]{display:block;background:#fff;border:1px solid var(--line);border-radius:8px;padding:8px;width:100%}
.hint{color:var(--muted);font-size:12px;margin-top:6px}
.ok{background:#eaf6e4;border:1px solid #cde8c6;border-radius:10px;padding:8px 10px;color:#234c1b}

.filters .select-wrap{display:flex;gap:8px;align-items:center}
.input, select, textarea{background:#fff;border:1px solid var(--line);border-radius:8px;padding:8px;min-width:160px;color:#0f172a;width:100%}
textarea.input{width:100%}

.btn{background:linear-gradient(180deg, var(--accent) 0%, var(--accent-2) 100%);border:1px solid #16a34a;color:#fff;border-radius:10px;padding:8px 12px;cursor:pointer;text-decoration:none}
.btn.secondary{background:#fff;color:var(--link);border:1px solid var(--link)}
.link-btn{text-decoration:none}

.chips{display:flex;flex-wrap:wrap;gap:8px}
.chip{background:var(--chip);border:1px solid var(--line);border-radius:999px;padding:6px 10px;cursor:pointer;font-size:12px}
.chip.on{background:#e6f4ff;border-color:#b6d9ff;color:#0a66c2}

.pill{display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;border:1px solid var(--line);background:#fff}

.table-wrap{max-height:70vh; overflow:auto; border:1px solid var(--line);border-radius:12px}
.tbl{width:100%;border-collapse:separate;border-spacing:0}
.tbl thead th{position:sticky;top:0;background:#f7fafc;border-bottom:1px solid var(--line);text-align:left;padding:10px;font-size:12px;color:#475569;z-index:1}
.tbl tbody tr:nth-child(odd){background:#fcfcfd} .tbl td{padding:10px 12px;border-bottom:1px solid #eef2f6;vertical-align:top}
.title{font-weight:600} .snippet{white-space:pre-wrap}

.multisel .chips{max-width:600px}

@media (prefers-color-scheme: dark){
  :root{--bg:#0b1220; --panel:#0f172a; --ink:#e5e7eb; --muted:#9aa3b2; --line:#1f2937; --chip:#0f172a}
  .tbl thead th{background:#0f172a;color:#cbd5e1}
  .tbl tbody tr:nth-child(odd){background:#0d1526}
  .tab{background:#0f172a}
  .tab.active{background:#0a223d}
  .btn.secondary{background:transparent}
}`;
  return <style>{css}</style>;
}
